// Wallet sign-in for API endpoints that need proof of address ownership
// (creator dashboard, collection edits, Twitter connect, admin dashboards).
//
// The wallet BIP-322 signs a human-readable sign-in message carrying a fresh
// nonce and timestamp. The API rebuilds the same message, checks the
// signature, burns the nonce and answers with a 24h session token. That token
// goes in the `signature` / `creator_signature` field those endpoints already
// take, so request shapes stay the same.
//
// This replaces the old signed dummy PSBT, which was cached forever and proved
// nothing about freshness or intent.

import { API_URL, STORAGE_AUTH_PSBT, STORAGE_AUTH_SESSION } from '@/utils/constants'
import { signMessageWithWallet } from '@/app/alkanes/lib/wallet-signature'
import Wallet from '@/utils/wallet'

export type AuthSession = {
	token: string
	address: string
	/** Unix seconds. */
	expires_at: number
}

/** Stop using a token this long before the API would reject it. */
const EXPIRY_MARGIN_SECS = 5 * 60

/** Must match `sign_in_message` in ordinals-api `src/utils/auth_session.rs`. */
export function signInMessage(address: string, nonce: string, issuedAtMs: number): string {
	return (
		'Sign in to Ordinals Wallet\n\n' +
		'This proves you own this address. It does not move funds or cost a fee.\n\n' +
		`Address: ${address}\n` +
		`Nonce: ${nonce}\n` +
		`Issued At: ${issuedAtMs}`
	)
}

export function newNonce(): string {
	const bytes = new Uint8Array(16)
	crypto.getRandomValues(bytes)
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** A stored session is usable only for the same address and before expiry. */
export function usableSession(
	session: AuthSession | null,
	address: string | undefined,
	nowSecs: number
): session is AuthSession {
	return (
		!!session &&
		!!address &&
		session.address === address &&
		typeof session.token === 'string' &&
		session.expires_at - EXPIRY_MARGIN_SECS > nowSecs
	)
}

function readSession(): AuthSession | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = localStorage.getItem(STORAGE_AUTH_SESSION)
		return raw ? (JSON.parse(raw) as AuthSession) : null
	} catch {
		return null
	}
}

/** The current session token, or null when the user needs to sign in. */
export function getSessionToken(): string | null {
	const session = readSession()
	const address = Wallet.getTaprootAddress()
	return usableSession(session, address, Date.now() / 1000) ? session.token : null
}

export function clearSession() {
	try {
		localStorage.removeItem(STORAGE_AUTH_SESSION)
		// The old dummy-PSBT credential is no longer used; drop any cached copy.
		localStorage.removeItem(STORAGE_AUTH_PSBT)
	} catch {}
}

/** Ask the wallet to sign in and store the session. Throws on refusal or rejection. */
export async function signIn(): Promise<string> {
	const address = Wallet.getTaprootAddress()
	if (!address) throw new Error('Connect a wallet to sign in.')

	const nonce = newNonce()
	const issuedAt = Date.now()
	const signature = await signMessageWithWallet(signInMessage(address, nonce, issuedAt))

	const response = await fetch(`${API_URL}/auth/session`, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ address, nonce, issued_at: issuedAt, signature })
	})
	const body = (await response.json().catch(() => null)) as
		| (AuthSession & { error?: unknown; message?: string })
		| null
	if (!response.ok || !body?.token) {
		throw new Error(body?.message || 'Sign-in failed. Try again.')
	}

	const session: AuthSession = {
		token: body.token,
		address: body.address,
		expires_at: body.expires_at
	}
	try {
		localStorage.setItem(STORAGE_AUTH_SESSION, JSON.stringify(session))
		localStorage.removeItem(STORAGE_AUTH_PSBT)
	} catch {}
	return session.token
}

/** The current token, signing in first if there is none. */
export async function ensureSession(): Promise<string> {
	return getSessionToken() ?? (await signIn())
}
